import { useState, useEffect } from 'react';
import { IoClose } from "react-icons/io5";

export default function InStationModal({ isOpen, onClose, onSubmit, editData, getAllInStationZones }) {
    const [formData, setFormData] = useState({
        min_weight: '',
        max_weight: '',
        baseRate: '',
        zoneName: ''
    });
    const [errors, setErrors] = useState({});
    const [inStationZones, setInStationZones] = useState([]);

    useEffect(() => {
        if (!isOpen) {
            setFormData({
                min_weight: '',
                max_weight: '',
                baseRate: '',
                zoneName: ''
            });
            setErrors({});
        } else if (editData) {
            setFormData({
                min_weight: editData.min_weight || '',
                max_weight: editData.max_weight || '',
                baseRate: editData.base_rate || '',
                zoneName: editData.zone_name || editData.zone?.zone_name || ''
            });
            setErrors({});
        }
    }, [isOpen, editData]);

    useEffect(() => {
        if (isOpen && getAllInStationZones) {
            getAllInStationZones(setInStationZones);
        }
    }, [isOpen, getAllInStationZones]);

    useEffect(() => {
        if (editData && inStationZones.length > 0) {
            const zoneName = editData.zone_name || editData.zone?.zone_name;
            if (zoneName) {
                setFormData(prev => ({
                    ...prev,
                    zoneName: zoneName
                }));
            }
        }
    }, [editData, inStationZones]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === 'zoneName') {
            setFormData(prev => ({ ...prev, [name]: value }));
        } else {
            const regex = /^\d*\.?\d*$/;
            if (regex.test(value) || value === '') {
                setFormData(prev => ({ ...prev, [name]: value }));
            }
        }

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.min_weight) {
            newErrors.min_weight = 'Minimum Weight is required';
        }

        if (!formData.max_weight) {
            newErrors.max_weight = 'Maximum Weight is required';
        }

        if (formData.min_weight && formData.max_weight) {
            const minWeight = parseFloat(formData.min_weight);
            const maxWeight = parseFloat(formData.max_weight);
            if (minWeight >= maxWeight) {
                newErrors.max_weight = 'Maximum Weight must be greater than Minimum Weight';
            }
        }

        if (!formData.baseRate) {
            newErrors.baseRate = 'Base Rate is required';
        }

        if (!formData.zoneName.trim()) {
            newErrors.zoneName = 'Zone Name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            // Find the selected zone to get its ID
            const selectedZone = inStationZones.find(zone => zone.zone_name === formData.zoneName);

            const submitData = {
                zone_id: selectedZone ? selectedZone.id : null,
                min_weight: parseFloat(formData.min_weight),
                max_weight: parseFloat(formData.max_weight),
                base_rate: parseFloat(formData.baseRate)
            };
            onSubmit(submitData);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-screen overflow-y-auto">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-800">
                        {editData ? "Edit In-Station Base Rate" : "Add In-Station Base Rate"}
                    </h2>
                    <button
                        className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
                        onClick={onClose}
                    >
                        <IoClose className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 font-medium mb-2">
                                Zone Name <span className="text-red-500">*</span>
                            </label>
                            <select
                                name="zoneName"
                                value={formData.zoneName}
                                onChange={handleInputChange}
                                disabled={!!editData}
                                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.zoneName ? 'border-red-500' : 'border-gray-300'
                                    } ${editData ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            >
                                <option value="">Select a zone</option>
                                {inStationZones && inStationZones.length > 0 ? (
                                    inStationZones.map((zone) => (
                                        <option key={zone.id} value={zone.zone_name}>
                                            {zone.zone_name}
                                        </option>
                                    ))
                                ) : (
                                    <option value="" disabled>No zones available</option>
                                )}
                            </select>
                            {errors.zoneName && (
                                <p className="text-red-500 text-sm mt-1">{errors.zoneName}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-gray-700 font-medium mb-2">
                                Base Rate <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="baseRate"
                                value={formData.baseRate}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.baseRate ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                placeholder="e.g., 1, 2, 3.4"
                            />
                            {errors.baseRate && (
                                <p className="text-red-500 text-sm mt-1">{errors.baseRate}</p>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-gray-700 font-medium mb-2">
                                Minimum Weight <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="min_weight"
                                value={formData.min_weight}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.min_weight ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                placeholder="e.g., 1, 2, 3.4"
                            />
                            {errors.min_weight && (
                                <p className="text-red-500 text-sm mt-1">{errors.min_weight}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-gray-700 font-medium mb-2">
                                Maximum Weight <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="max_weight"
                                value={formData.max_weight}
                                onChange={handleInputChange}
                                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.max_weight ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                placeholder="e.g., 1, 2, 3.4"
                            />
                            {errors.max_weight && (
                                <p className="text-red-500 text-sm mt-1">{errors.max_weight}</p>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn btn-reset-filter justify-center"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className={`btn justify-center ${
                                editData ? "btn-edit text-white" : "btn-primary"
                            }`}
                        >
                            {editData ? "Update" : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}